import type React from 'react'
import { Stack, Alert, Divider, Typography } from '@mui/material'
import { Favorite } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

import CartItemList from '../CartItem/CartItemList'
import AllergensInfo from '../../shared/AllergensInfo'
import SubtotalLine from './SubtotalLine'
import type { CanteenCartItem } from '../types'
import type { FragmentType } from '@/generated/gql'
import { graphql, useFragment } from '@/generated/gql'
import useCharityMeals from '@/helpers/features/charityMeals'
import { numberFormat } from '@/helpers/numbers'

const canteenCartDetailsUserDataFragment = graphql(`
  fragment CanteenCartDetails_UserDataFragment on User {
    subsidySettings {
      unlimitedSubsidy
    }
    account {
      topUpEnabled
      userInterfaceSettings {
        hideCCPrices
      }
    }
    wallet {
      balance
    }
  }
`)

type Props = {
  availableSubsidy: number
  userData: FragmentType<typeof canteenCartDetailsUserDataFragment>
  error: string | null
  onDismissError: () => void
  onUpdate: (item: CanteenCartItem) => void
  onRemove: (itemReference: string) => void
  cartItems: CanteenCartItem[]
  cartTotal: number
}

const Details: React.FC<Props> = ({
  availableSubsidy,
  userData: userDataProp,
  cartItems,
  onUpdate,
  onRemove,
  onDismissError,
  error,
  cartTotal,
}) => {
  const userData = useFragment(canteenCartDetailsUserDataFragment, userDataProp)

  const charityMeals = useCharityMeals()
  const { t } = useTranslation()

  const mainCount = cartItems.reduce((acc, cartItem) => {
    return acc + (cartItem.item.type === 'main' ? cartItem.qty : 0)
  }, 0)

  const itemCount = cartItems.reduce((acc, cartItem) => acc + cartItem.qty, 0)
  const subtotal = cartTotal
  const amountSubsidised = Math.min(subtotal, availableSubsidy)
  const amountToPay = subtotal - amountSubsidised
  const creditsToUse = Math.min(amountToPay, userData.wallet.balance)

  const topUpEnabled = Boolean(userData.account?.topUpEnabled)
  const unlimitedSubsidy = Boolean(userData.subsidySettings.unlimitedSubsidy)
  const hidePrices = Boolean(userData.account?.userInterfaceSettings.hideCCPrices)

  const userWalletBalance = userData.account?.topUpEnabled ? Number(userData.wallet.balance) : 0
  const insufficientCredits =
    !unlimitedSubsidy && subtotal > Number(availableSubsidy) + userWalletBalance
  const shouldShowSubsidyAlert = !topUpEnabled && insufficientCredits

  return (
    <Stack
      flex={1}
      bgcolor="background.paper"
      sx={{ marginTop: 2, minHeight: 0, display: 'flex', flexDirection: 'column' }}
    >
      <Stack spacing={2} flex={1} sx={{ overflow: 'auto' }}>
        <Typography variant="body1" paddingX={3}>
          {t('cc:orderSummary')}
        </Typography>
        {cartItems.length > 0 ? (
          <>
            <Stack spacing={2} direction="column" paddingX={3}>
              {error && (
                <Alert severity="error" onClose={() => onDismissError()}>
                  {error}
                </Alert>
              )}

              <CartItemList
                onRemove={onRemove}
                hidePrices={hidePrices}
                cartItems={cartItems}
                onUpdate={onUpdate}
              />

              <AllergensInfo />
            </Stack>
            <Divider />
            <Stack direction="column" spacing={2} marginTop={2} paddingX={3}>
              {!hidePrices ? (
                <SubtotalLine
                  left={t('cc:subtotalWithItems', { count: itemCount })}
                  right={`${numberFormat(subtotal)} ${t('cc:credits').toLowerCase()}`}
                />
              ) : null}

              {unlimitedSubsidy && !hidePrices ? (
                <SubtotalLine
                  left={t('cc:unlimitedSubsidy')}
                  right={`- ${t('cc:creditsCount', { count: subtotal })}`}
                  color="primary.main"
                />
              ) : (
                <>
                  {amountSubsidised > 0 && !hidePrices ? (
                    <SubtotalLine
                      left={t('cc:freeCredits')}
                      right={`- ${numberFormat(amountSubsidised)} ${t('cc:credits').toLowerCase()}`}
                      color="primary.main"
                      emphasise={true}
                    />
                  ) : null}

                  {creditsToUse > 0 && !hidePrices && topUpEnabled ? (
                    <SubtotalLine
                      left={t('cc:personalCredits')}
                      right={`- ${numberFormat(creditsToUse)} ${t('cc:credits').toLowerCase()}`}
                    />
                  ) : null}

                  {!hidePrices ? <SubtotalLine left={t('delivery')} right={t('free')} /> : null}
                </>
              )}
              {mainCount > 0 && charityMeals.enabled && (
                <SubtotalLine
                  left={t('cc:mealsToChild', { count: mainCount })}
                  right={<Favorite color="info" fontSize="small" />}
                  color="info.main"
                />
              )}
            </Stack>
          </>
        ) : null}
      </Stack>
      {shouldShowSubsidyAlert && (
        <Alert severity="info" sx={{ alignItems: 'center', marginX: 3, marginBottom: 2 }}>
          {t('cc:insufficientCreditsAlert')}
        </Alert>
      )}
    </Stack>
  )
}

export default Details
